import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Phone, MapPin, MessageCircle } from 'lucide-react';
import { PageHero, PillButton } from '@/components/ui-bits';
import { Reveal } from '@/components/motion';
import { OrnamentHeart } from '@/components/Ornaments';
import { PHONE, WHATSAPP } from '@/i18n';
import { api } from '@/lib/api';

const inputCls = 'w-full rounded-2xl border border-input bg-beige/60 px-5 py-4 text-lg text-textDark placeholder:text-textDark/40 focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/25 transition-colors';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', { ...form, lang });
      toast.success(t('form.success'));
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch {
      toast.error(t('form.error'));
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow={t('nav.contact')}
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        ornament={<OrnamentHeart className="hidden md:block absolute right-16 top-20 w-32 text-terracotta/25" />}
      />

      <section className="px-5 md:px-10 pb-24">
        <div className="max-w-[1180px] mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
          <Reveal>
            <div className="bg-rosa rounded-3xl p-8 md:p-10 flex flex-col gap-6">
              <a href={`tel:+39${PHONE.replace(/\s/g, '')}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-14 h-14 rounded-2xl bg-terracotta/15 text-terracotta flex items-center justify-center"><Phone size={24} /></span>
                <span><span className="block text-sm text-textDark/60">{t('contact.phone')}</span><span className="font-serif text-2xl text-textDark group-hover:text-terracotta transition-colors">{PHONE}</span></span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-2xl bg-salvia/20 text-salvia flex items-center justify-center"><MapPin size={24} /></span>
                <span><span className="block text-sm text-textDark/60">{t('contact.area')}</span><span className="font-serif text-2xl text-textDark">{t('contact.areaValue')}</span></span>
              </div>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
                <span className="w-14 h-14 rounded-2xl bg-salvia text-textDark flex items-center justify-center"><MessageCircle size={24} /></span>
                <span><span className="block text-sm text-textDark/60">WhatsApp</span><span className="font-serif text-2xl text-textDark group-hover:text-terracotta transition-colors">{PHONE}</span></span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="bg-card rounded-3xl p-8 md:p-10 border border-terracotta/10">
              <h2 className="font-serif text-3xl text-textDark mb-6">{t('contact.formTitle')}</h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <input required value={form.name} onChange={upd('name')} data-testid="contact-name" className={inputCls} placeholder={t('form.name')} />
                <input value={form.phone} onChange={upd('phone')} data-testid="contact-phone-input" className={inputCls} placeholder={t('form.phone')} />
              </div>
              <input required type="email" value={form.email} onChange={upd('email')} data-testid="contact-email" className={`${inputCls} mb-4`} placeholder={t('form.email')} />
              <textarea required rows={5} value={form.message} onChange={upd('message')} data-testid="contact-message" className={`${inputCls} mb-6 resize-none`} placeholder={t('form.message')} />
              <PillButton type="submit" disabled={loading} testId="contact-submit">{loading ? t('common.sending') : t('common.send')}</PillButton>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
